import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDeals } from '../context/DataContext';
import { usePitches, PITCH_STATUSES } from '../context/PitchesContext';
import { fmtMoney } from '../lib/format';
import { Plus, Search, Trash2, ChevronDown, MessageSquare, Upload, X, Minus } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import InitialsAvatar from '../components/InitialsAvatar';
import { Textarea } from '../components/ui/textarea';
import BrandSelect from '../components/BrandSelect';
import ContactSelect from '../components/ContactSelect';
import PitchTalentPicker from '../components/pitches/PitchTalentPicker';
import { Stars } from '../components/PitchRow';
import QuickAddPitch from '../components/pitches/QuickAddPitch';
import DateRangeFilter from '../components/DateRangeFilter';
import { filterByRange } from '../lib/dateRange';
import TalentPitchDashboard from '../components/TalentPitchDashboard';
import MasterPitchingGrid from '../components/pitching/MasterPitchingGrid';
import StickersRail from '../components/pitching/StickersRail';
import { STICKERS } from '../lib/stickers';
import { useBrandPitches } from '../context/BrandPitchesContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const StatusPill = ({ status }) => {
  const s = PITCH_STATUSES.find((x) => x.id === status);
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700 whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
      {s?.label}
    </span>
  );
};

const todayISO = () => new Date().toISOString().slice(0, 10);
// "2026-09" -> "September 26"
const monthYearLabel = (ym) => {
  if (!ym) return null;
  const d = new Date(`${ym}-01T00:00:00`);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { month: 'long', year: '2-digit' });
};

const emptyPitch = (talentId = null) => ({
  title: '', talent_id: talentId, talent_ids: talentId ? [talentId] : [], brand_id: '', contact_id: null, status: 'pitched',
  pitched_date: todayISO(), last_contact_date: todayISO(), notes: '', note_entries: [],
});

const NOTE_PALETTES = [
  { bg: '#fce7f3', border: '#f9a8c8', ink: '#9d174d' },  // pink
  { bg: '#ede9fe', border: '#c4b5fd', ink: '#5b21b6' },  // lilac
  { bg: '#dcfce7', border: '#86efac', ink: '#166534' },  // mint
  { bg: '#fef3c7', border: '#fde68a', ink: '#92400e' },  // amber
  { bg: '#dcecfa', border: '#a9c8eb', ink: '#1e3a5b' },  // sky
  { bg: '#ffe4d1', border: '#f5c199', ink: '#7c2d12' },  // peach
];

// ------- Board (kanban) view: brands pitched, grouped by star rating -------
const STAR_LANES = [
  { id: 0, label: 'unrated', color: '#94a3b8' },
  { id: 1, label: '1 star', color: '#f59e0b' },
  { id: 2, label: '2 stars', color: '#f59e0b' },
  { id: 3, label: '3 stars', color: '#d394ab' },
  { id: 4, label: '4 stars', color: '#c026d3' },
  { id: 5, label: '5 stars', color: '#059669' },
];

const PitchBoardCard = ({ pitch, brand, taggedTalents, onClick, dragProps, stars, onStars, onConvert }) => {
  const notes = Array.isArray(pitch.note_entries) ? [...pitch.note_entries].filter((n) => !n.hidden_from_tracker).sort((a, b) => (b.date || '').localeCompare(a.date || '')) : [];
  const latest = notes[0];
  return (
    <div
      {...dragProps}
      onClick={onClick}
      className="group border rounded-lg p-2.5 bg-white border-slate-200 hover:border-pink-400 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
      data-testid={`pitch-board-card-${pitch.id}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold shrink-0"
          style={{ background: '#f3ecfb', color: '#5b21b6', border: '1px solid #d3c1e1' }}
          title={brand?.name || 'no brand'}
        >
          {brand?.logo || (brand?.name || '?').slice(0, 1).toUpperCase()}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-slate-800 truncate">{brand?.name || 'no brand'}</div>
          <div className="text-[11px] text-slate-400 truncate">{pitch.title}</div>
        </div>
      </div>
      {taggedTalents.length > 0 && (
        <div className="mt-2 flex items-center -space-x-1.5" title={taggedTalents.map((t) => t.name).join(', ')}>
          {taggedTalents.slice(0, 4).map((t) => (
            <div key={t.user_id} className="ring-2 ring-white rounded-full">
              <InitialsAvatar name={t.name} size={18} />
            </div>
          ))}
          {taggedTalents.length > 4 && <span className="ml-1.5 text-[10px] text-slate-500 font-medium">+{taggedTalents.length - 4}</span>}
        </div>
      )}
      {latest ? (
        <div className="mt-2 rounded-lg px-2 py-1.5 text-[11px] leading-snug" style={{ background: NOTE_PALETTES[0].bg, color: NOTE_PALETTES[0].ink }}>
          <span className="line-clamp-2">{latest.text}</span>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-1 text-[10px] text-slate-400 italic"><MessageSquare className="w-3 h-3" /> no notes yet</div>
      )}
      {notes.length > 1 && <div className="mt-1 text-[10px] text-slate-400">+{notes.length - 1} more note{notes.length - 1 > 1 ? 's' : ''}</div>}
      <div className="mt-2 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
        <Stars value={stars} onChange={onStars} testId={`board-stars-${pitch.id}`} />
        <button
          type="button"
          onClick={onConvert}
          className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-800 opacity-0 group-hover:opacity-100 transition"
          title="convert to deal"
          data-testid={`pitch-board-convert-${pitch.id}`}
        >convert to deal →</button>
      </div>
    </div>
  );
};

const PitchBoardColumn = ({ status, pitches, brandMap, talent, ratingsFor, onCardClick, onDrop, onStars, onConvert, dragging, setDragging }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`w-64 shrink-0 flex flex-col rounded-xl border overflow-hidden ${over ? 'border-pink-400 shadow-lg shadow-pink-100' : 'border-slate-200'} transition-all bg-white`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(status.id); }}
      data-testid={`pitch-board-column-${status.id}`}
    >
      <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg,#d394ab 0%,#e9c2d5 60%,#d3c1e1 100%)' }} />
      <div className="px-3 py-3 flex items-center justify-between bg-gradient-to-b from-pink-50/60 to-white">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: status.color }} />
          <h3 className="text-sm font-bold lowercase" style={{ color: '#8a2c5b' }}>{status.label}</h3>
        </div>
        <span className="text-xs font-semibold inline-flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5" style={{ background: '#d394ab', color: 'white' }}>{pitches.length}</span>
      </div>
      <div className="flex-1 p-2 space-y-1.5 overflow-y-auto bg-slate-50/40" style={{ minHeight: 200 }}>
        {pitches.map((p) => {
          const brand = brandMap[p.brand_id];
          const ids = Array.isArray(p.talent_ids) && p.talent_ids.length ? p.talent_ids : (p.talent_id ? [p.talent_id] : []);
          const taggedTalents = ids.map((tid) => talent.find((t) => t.user_id === tid)).filter(Boolean);
          const revTalentId = p.talent_id || ids[0];
          const stars = (revTalentId && p.brand_id) ? (ratingsFor[`${p.brand_id}::${revTalentId}`] || 0) : 0;
          return (
            <PitchBoardCard
              key={p.id}
              pitch={p}
              brand={brand}
              taggedTalents={taggedTalents}
              stars={stars}
              onStars={revTalentId ? (v) => onStars(p.brand_id, revTalentId, v) : undefined}
              onConvert={(e) => { e.stopPropagation(); onConvert(p); }}
              onClick={() => onCardClick(p)}
              dragProps={{
                draggable: true,
                onDragStart: () => setDragging(p.id),
                onDragEnd: () => setDragging(null),
                style: { opacity: dragging === p.id ? 0.5 : 1 },
              }}
            />
          );
        })}
        {pitches.length === 0 && (
          <div className="text-center text-xs text-slate-400 py-6 border border-dashed border-slate-200 rounded-lg">drop pitches here</div>
        )}
      </div>
    </div>
  );
};

const PitchBoard = ({ pitches, brandMap, talent, ratingsFor, onCardClick, onStars, onConvert }) => {
  const [dragging, setDragging] = useState(null);
  const ratingOf = (p) => {
    const ids = Array.isArray(p.talent_ids) && p.talent_ids.length ? p.talent_ids : (p.talent_id ? [p.talent_id] : []);
    const revTalentId = p.talent_id || ids[0];
    return (revTalentId && p.brand_id) ? (ratingsFor[`${p.brand_id}::${revTalentId}`] || 0) : 0;
  };
  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex gap-3 min-w-max">
        {STAR_LANES.map((status) => (
          <PitchBoardColumn
            key={status.id}
            status={status}
            pitches={pitches.filter((p) => ratingOf(p) === status.id)}
            brandMap={brandMap}
            talent={talent}
            ratingsFor={ratingsFor}
            onCardClick={onCardClick}
            onStars={onStars}
            onConvert={onConvert}
            onDrop={(laneId) => {
              if (!dragging) return;
              const p = pitches.find((x) => x.id === dragging);
              if (!p) return;
              const ids = Array.isArray(p.talent_ids) && p.talent_ids.length ? p.talent_ids : (p.talent_id ? [p.talent_id] : []);
              const revTalentId = p.talent_id || ids[0];
              if (revTalentId && p.brand_id) onStars(p.brand_id, revTalentId, laneId);
            }}
            dragging={dragging}
            setDragging={setDragging}
          />
        ))}
      </div>
    </div>

  );
};

const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MON_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const NoteBubble = ({ note, idx, onPatch, onRemove }) => {
  const p = NOTE_PALETTES[idx % NOTE_PALETTES.length];
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(note.text || '');
  const [showDate, setShowDate] = React.useState(false);
  const [editingLabel, setEditingLabel] = React.useState(false);
  const [labelDraft, setLabelDraft] = React.useState(note.label || '');
  const [newComment, setNewComment] = React.useState('');
  const [addingComment, setAddingComment] = React.useState(false);
  React.useEffect(() => { setDraft(note.text || ''); }, [note.text]);
  React.useEffect(() => { setLabelDraft(note.label || ''); }, [note.label]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (note.text || '')) onPatch({ text: next });
  };
  const commitLabel = () => {
    setEditingLabel(false);
    const next = labelDraft.trim().slice(0, 24);
    if (next !== (note.label || '')) onPatch({ label: next });
  };

  const dt = note.date ? (() => { const [y,m,d] = note.date.split('-').map(Number); return (y && m && d) ? new Date(y, m-1, d) : null; })() : null;
  const dateLabel = dt ? `${DOW[dt.getDay()]} ${dt.getDate()} ${MON_SHORT[dt.getMonth()]}` : 'no date';

  const comments = Array.isArray(note.comments) ? note.comments : [];
  const addComment = () => {
    const text = newComment.trim();
    if (!text) return;
    const c = { id: 'pnc_' + Math.random().toString(36).slice(2, 8), date: new Date().toISOString().slice(0, 10), text };
    onPatch({ comments: [...comments, c] });
    setNewComment('');
    setAddingComment(false);
  };
  const patchComment = (cid, patch) => onPatch({ comments: comments.map((c) => (c.id === cid ? { ...c, ...patch } : c)) });
  const removeComment = (cid) => onPatch({ comments: comments.filter((c) => c.id !== cid) });

  return (
    <li
      className={`relative rounded-2xl p-2.5 group shadow-sm ${note.hidden_from_tracker ? 'opacity-55' : ''}`}
      style={{ background: p.bg, border: `1px solid ${p.border}` }}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {editingLabel ? (
          <input
            autoFocus
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setLabelDraft(note.label || ''); setEditingLabel(false); } }}
            placeholder="e.g. spf"
            maxLength={24}
            className="h-5 w-20 text-[10px] font-bold uppercase tracking-wide bg-white/70 rounded px-1.5 border-none outline-none"
            style={{ color: p.ink }}
            data-testid={`pitch-note-label-input-${note.id}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingLabel(true)}
            className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wide"
            style={{ background: 'rgba(255,255,255,0.6)', color: p.ink }}
            title="click to set a short label, e.g. spf"
            data-testid={`pitch-note-label-btn-${note.id}`}
          >{note.label || '+ label'}</button>
        )}
        <button
          type="button"
          onClick={() => setShowDate((v) => !v)}
          className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold tracking-wide"
          style={{ background: 'rgba(255,255,255,0.6)', color: p.ink }}
          title="click to change date"
          data-testid={`pitch-note-date-btn-${note.id}`}
        >{dateLabel}</button>
        {note.hidden_from_tracker && (
          <span className="inline-flex items-center h-5 px-1.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-white/60 text-slate-500" title="not shown in the tracker's note chips">hidden</span>
        )}
        {showDate && (
          <input
            type="date"
            value={note.date || ''}
            onChange={(e) => { onPatch({ date: e.target.value }); setShowDate(false); }}
            onBlur={() => setShowDate(false)}
            autoFocus
            className="h-5 text-[10px] bg-white/70 rounded border-none outline-none px-1"
            style={{ color: p.ink }}
            data-testid={`pitch-note-date-${note.id}`}
          />
        )}
        <button
          type="button"
          onClick={() => onPatch({ hidden_from_tracker: !note.hidden_from_tracker })}
          className={`ml-auto hover:text-amber-600 transition ${note.hidden_from_tracker ? '' : 'opacity-0 group-hover:opacity-100'}`}
          style={{ color: p.ink }}
          title={note.hidden_from_tracker ? 'show in tracker view again' : 'hide from tracker view (keeps it in this chain)'}
          data-testid={`pitch-note-hide-${note.id}`}
        >{note.hidden_from_tracker ? <Plus className="w-3 h-3" /> : <Minus className="w-3 h-3" />}</button>
        <button
          type="button"
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 hover:text-red-600 transition"
          style={{ color: p.ink }}
          title="remove"
          data-testid={`pitch-note-delete-${note.id}`}
        ><Trash2 className="w-3 h-3" /></button>
      </div>
      {editing ? (
        <Textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(note.text || ''); setEditing(false); } }}
          rows={2}
          className="text-xs bg-white/70 border-transparent focus-visible:ring-2 focus-visible:ring-pink-300 resize-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-left w-full whitespace-pre-wrap leading-relaxed cursor-text"
          style={{ color: p.ink }}
          title="click to edit"
        >{note.text || <span className="italic opacity-60">empty note — click to edit</span>}</button>
      )}

      {/* Thread of follow-up comments on this note */}
      {(comments.length > 0 || addingComment) && (
        <div className="mt-2 pl-3 border-l-2 space-y-1.5" style={{ borderColor: `${p.border}` }}>
          {comments.map((c) => (
            <CommentPill key={c.id} comment={c} ink={p.ink} onPatch={(patch) => patchComment(c.id, patch)} onRemove={() => removeComment(c.id)} />
          ))}
          {addingComment && (
            <div className="flex gap-1.5">
              <Input
                autoFocus
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addComment(); } if (e.key === 'Escape') { setNewComment(''); setAddingComment(false); } }}
                onBlur={() => { if (!newComment.trim()) setAddingComment(false); }}
                placeholder="add a follow-up…"
                className="h-7 text-[11px] bg-white/70 border-transparent"
              />
              <Button onClick={addComment} disabled={!newComment.trim()} className="h-7 text-[10px] bg-pink-500 hover:bg-pink-600 text-white px-2 shrink-0">save</Button>
            </div>
          )}
        </div>
      )}
      {!addingComment && (
        <button
          type="button"
          onClick={() => setAddingComment(true)}
          className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold opacity-70 hover:opacity-100 transition"
          style={{ color: p.ink }}
          data-testid={`add-comment-${note.id}`}
        ><Plus className="w-2.5 h-2.5" /> add comment</button>
      )}
    </li>
  );
};

const CommentPill = ({ comment, ink, onPatch, onRemove }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(comment.text || '');
  const [showDate, setShowDate] = React.useState(false);
  React.useEffect(() => { setDraft(comment.text || ''); }, [comment.text]);
  const dt = comment.date ? (() => { const [y,m,d] = comment.date.split('-').map(Number); return (y && m && d) ? new Date(y, m-1, d) : null; })() : null;
  const dateLabel = dt ? `${DOW[dt.getDay()]} ${dt.getDate()} ${MON_SHORT[dt.getMonth()]}` : 'no date';
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (comment.text || '')) onPatch({ text: next });
  };
  return (
    <div className="flex items-start gap-1.5 group/comment">
      <button
        type="button"
        onClick={() => setShowDate((v) => !v)}
        className="inline-flex items-center h-4 px-1.5 rounded-full text-[9px] font-bold tabular-nums shrink-0 mt-0.5"
        style={{ background: 'rgba(255,255,255,0.6)', color: ink }}
      >{dateLabel}</button>
      {showDate && (
        <input
          type="date"
          value={comment.date || ''}
          onChange={(e) => { onPatch({ date: e.target.value }); setShowDate(false); }}
          onBlur={() => setShowDate(false)}
          autoFocus
          className="h-5 text-[10px] bg-white/70 rounded border-none outline-none px-1"
        />
      )}
      {editing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { setDraft(comment.text || ''); setEditing(false); } }}
          className="h-6 text-[11px] bg-white/70 border-transparent flex-1"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-[11px] leading-snug text-left flex-1 min-w-0 whitespace-pre-wrap"
          style={{ color: ink }}
        >{comment.text || <span className="italic opacity-60">empty</span>}</button>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="opacity-0 group-hover/comment:opacity-100 shrink-0 hover:text-red-600"
        style={{ color: ink }}
        title="remove"
      ><Trash2 className="w-3 h-3" /></button>
    </div>
  );
};

const PitchDialog = ({ open, onOpenChange, pitch, defaultTalentId, ratingsFor, onStars }) => {
  const { addPitch, updatePitch, removePitch } = usePitches();
  const { talent, brands } = useDeals();
  const isEdit = Boolean(pitch?.id);
  const [form, setForm] = React.useState(emptyPitch(defaultTalentId));
  const [saving, setSaving] = React.useState(false);
  const [newNote, setNewNote] = React.useState('');
  const [newNoteLabel, setNewNoteLabel] = React.useState('');

  React.useEffect(() => {
    if (pitch) {
      // Backfill any legacy free-text note into the timestamped log on first open
      let entries = Array.isArray(pitch.note_entries) ? [...pitch.note_entries] : [];
      if (!entries.length && (pitch.notes || '').trim()) {
        entries = [{ id: 'legacy', date: (pitch.pitched_date || new Date().toISOString().slice(0, 10)), text: pitch.notes.trim() }];
      }
      const ids = Array.isArray(pitch.talent_ids) && pitch.talent_ids.length
        ? pitch.talent_ids
        : (pitch.talent_id ? [pitch.talent_id] : []);
      // Queued items from mass upload have no real status yet — default to 'pitched' the
      // moment someone opens one to actually complete it.
      setForm({ ...pitch, talent_ids: ids, note_entries: entries });
    } else {
      setForm(emptyPitch(defaultTalentId));
    }
    setNewNote('');
    setNewNoteLabel('');
  }, [pitch, defaultTalentId, open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setTalents = (ids) => setForm((p) => ({ ...p, talent_ids: ids, talent_id: ids[0] || null }));
  const selectedBrand = brands.find((b) => b.id === form.brand_id);

  const addNote = () => {
    const text = newNote.trim();
    const label = newNoteLabel.trim().slice(0, 24);
    if (!text && !label) return;
    const entry = { id: 'pn_' + Math.random().toString(36).slice(2, 8), date: new Date().toISOString().slice(0, 10), text, label };
    persistNotes([entry, ...(form.note_entries || [])]);
    setNewNote('');
    setNewNoteLabel('');
  };
  // Notes are logged immediately — "log" means saved, not staged for the outer save button.
  const persistNotes = (next) => {
    set('note_entries', next);
    if (isEdit) updatePitch(pitch.id, { note_entries: next }).catch(() => toast.error('failed to save note'));
  };
  const patchNote = (id, patch) => persistNotes((form.note_entries || []).map((n) => (n.id === id ? { ...n, ...patch } : n)));
  const removeNote = (id) => persistNotes((form.note_entries || []).filter((n) => n.id !== id));

  const save = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      if (isEdit) await updatePitch(pitch.id, form);
      else await addPitch(form);
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-0 overflow-hidden border-pink-200 shadow-2xl">
        <div className="bg-gradient-to-br from-pink-100 via-pink-50 to-white px-4 py-3 border-b border-pink-200 flex items-center gap-2">
          <span className="text-lg">💌</span>
          <DialogTitle className="text-sm font-semibold text-pink-700 tracking-tight">
            {isEdit ? 'edit pitch' : 'new pitch'}
          </DialogTitle>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-pink-500">subject</Label>
            <div className="mt-1 bg-pink-50 border-2 border-pink-200 rounded-2xl px-3 py-2">
              <Input
                autoFocus
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="e.g. AW campaign pitch"
                className="h-7 px-0 text-sm font-semibold text-pink-800 bg-transparent border-none outline-none focus-visible:ring-0 placeholder:text-pink-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {isEdit && (form.talent_ids || []).length > 0 ? (
              <div className="col-span-2 flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-widest text-pink-500">rating</Label>
                <Stars
                  value={ratingsFor?.[`${form.brand_id}::${form.talent_id || (form.talent_ids || [])[0]}`] || 0}
                  onChange={(v) => onStars?.(form.brand_id, form.talent_id || (form.talent_ids || [])[0], v)}
                  size="lg"
                  testId="pitch-dialog-stars"
                />
              </div>
            ) : (
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-pink-500">roster</Label>
                <div className="mt-1">
                  <PitchTalentPicker talent={talent} selectedIds={form.talent_ids || []} onChange={setTalents} />
                </div>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-pink-500">brand</Label>
              <div className="mt-1 bg-lilac-50 border-2 border-lilac-200 rounded-2xl px-3 py-2" style={{ background: '#f3ecfb', borderColor: '#d3c1e1' }}>
                <BrandSelect
                  value={form.brand_id}
                  onChange={(v) => { set('brand_id', v); set('contact_id', null); }}
                  placeholder="pick brand"
                  className="[&_button]:h-7 [&_button]:px-0 [&_button]:bg-transparent [&_button]:border-none [&_button]:shadow-none [&_button]:text-sm [&_button]:font-semibold [&_button]:text-lilac-800"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-pink-500">contact</Label>
              <div className="mt-1 rounded-2xl px-3 py-2 border-2" style={{ background: '#f3ecfb', borderColor: '#d3c1e1' }}>
                <ContactSelect brand={selectedBrand} value={form.contact_id || ''} onChange={(v) => set('contact_id', v || null)} />
              </div>
            </div>
          </div>

          {/* Timestamped notes log */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-pink-500">notes log</Label>
            <div className="mt-1 flex gap-1.5">
              <Input
                value={newNoteLabel}
                onChange={(e) => setNewNoteLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }}
                placeholder="label, e.g. spf"
                maxLength={24}
                className="h-9 text-xs border-pink-200 focus-visible:ring-pink-300 w-24 shrink-0"
                data-testid="add-pitch-note-label"
              />
              <Input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }}
                placeholder="what you sent, brand response, next step…"
                className="h-9 text-xs border-pink-200 focus-visible:ring-pink-300"
              />
              <Button
                onClick={addNote}
                disabled={!newNote.trim() && !newNoteLabel.trim()}
                className="h-9 bg-pink-500 hover:bg-pink-600 text-white text-xs px-3 shrink-0"
                data-testid="add-pitch-note"
              >
                <Plus className="w-3 h-3 mr-1" /> log
              </Button>
            </div>

            {(form.note_entries || []).length === 0 ? (
              <div className="text-[11px] text-slate-400 italic mt-2">no notes yet — every entry is date-stamped</div>
            ) : (
              <ul className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
                {(form.note_entries || []).map((n, idx) => (
                  <NoteBubble key={n.id} note={n} idx={idx} onPatch={(patch) => patchNote(n.id, patch)} onRemove={() => removeNote(n.id)} />
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter className="px-4 pb-4 pt-0 flex-row justify-between gap-2 sm:justify-between">
          <div>
            {isEdit && (
              <Button
                variant="ghost"
                className="text-red-500 hover:bg-red-50 text-xs h-8"
                onClick={async () => { if (window.confirm('Delete this pitch?')) { await removePitch(pitch.id); onOpenChange(false); } }}
              >delete</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-8 text-xs">cancel</Button>
            <Button
              disabled={saving || !form.title}
              onClick={save}
              className="h-8 text-xs bg-pink-500 hover:bg-pink-600 text-white font-semibold"
            >{saving ? 'saving…' : (isEdit ? 'save' : 'create')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// "last campaign" table cell — shows the month/year this talent last worked with the brand.
// Auto-derived from confirmed deal history (`autoDate`), or a manual override on the pitch
// itself (`pitch.last_campaign`, format "YYYY-MM") when there's no deal data to pull from.
const LastCampaignCell = ({ pitch, autoDate }) => {
  const { updatePitch } = usePitches();
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState('');
  const manual = pitch.last_campaign || '';
  const effective = manual || (autoDate ? autoDate.slice(0, 7) : '');
  const label = monthYearLabel(effective);

  const startEdit = (e) => {
    e.stopPropagation();
    setVal(effective || new Date().toISOString().slice(0, 7));
    setEditing(true);
  };
  const save = async () => {
    setEditing(false);
    if (!val || val === manual) return;
    await updatePitch(pitch.id, { last_campaign: val }).catch(() => toast.error('failed to save'));
  };
  const clear = async (e) => {
    e.stopPropagation();
    setEditing(false);
    await updatePitch(pitch.id, { last_campaign: null }).catch(() => toast.error('failed to clear'));
  };

  if (editing) {
    return (
      <input
        type="month"
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditing(false); }}
        onClick={(e) => e.stopPropagation()}
        className="h-7 text-[11px] border border-sky-200 rounded px-1.5 outline-none focus:ring-2 focus:ring-sky-200"
        data-testid={`last-campaign-input-${pitch.id}`}
      />
    );
  }

  if (!label) {
    return (
      <button
        type="button"
        onClick={startEdit}
        title="set the month/year they last worked with this brand"
        className="shrink-0 w-5 h-5 rounded-full border border-slate-200 text-slate-400 hover:bg-slate-50 hover:border-slate-300 flex items-center justify-center transition"
        data-testid={`last-campaign-add-${pitch.id}`}
      ><Plus className="w-3 h-3" /></button>
    );
  }
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={startEdit}
      className="group inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-bold uppercase tracking-wide cursor-pointer hover:brightness-95 transition"
      style={{ background: '#dcecfa', color: '#1e3a5b', border: '1px solid #a9c8eb' }}
      title={manual ? 'manually set — click to edit' : 'from deal history — click to override'}
      data-testid={`last-campaign-chip-${pitch.id}`}
    >
      {label}
      {manual && <X className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" onClick={clear} data-testid={`last-campaign-clear-${pitch.id}`} />}
    </span>
  );
};


// Lightweight, always-saved popup for one pitch's notes chain — opened by clicking a note
// chip in the list view. A brand only ever has ONE pitch row, so every log here is just a
// dated note appended to this same pitch.
const NotesChainDialog = ({ open, onOpenChange, pitch, brand }) => {
  const { updatePitch } = usePitches();
  const [entries, setEntries] = React.useState([]);
  const [newNote, setNewNote] = React.useState('');
  const [newNoteLabel, setNewNoteLabel] = React.useState('');

  React.useEffect(() => {
    if (pitch) {
      let list = Array.isArray(pitch.note_entries) ? [...pitch.note_entries] : [];
      if (!list.length && (pitch.notes || '').trim()) {
        list = [{ id: 'legacy', date: (pitch.pitched_date || todayISO()), text: pitch.notes.trim() }];
      }
      setEntries(list);
    } else {
      setEntries([]);
    }
    setNewNote(''); setNewNoteLabel('');
  }, [pitch, open]);

  // Every "log" / edit / delete here saves immediately — this popup has no separate save step.
  // A brand only ever has ONE pitch row in the tracker, so every log here is just a note on
  // this same pitch — never a new pitch line (avoids duplicating the brand in the tracker).
  const persist = (next) => {
    setEntries(next);
    if (pitch?.id) updatePitch(pitch.id, { note_entries: next }).catch(() => toast.error('failed to save note'));
  };
  const addNote = () => {
    const text = newNote.trim();
    const label = newNoteLabel.trim().slice(0, 24);
    if (!text && !label) return;
    const entry = { id: 'pn_' + Math.random().toString(36).slice(2, 8), date: todayISO(), text, label };
    persist([entry, ...entries]);
    setNewNote(''); setNewNoteLabel('');
  };
  const patchNote = (id, patch) => persist(entries.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  const removeNote = (id) => persist(entries.filter((n) => n.id !== id));

  if (!pitch) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-0 overflow-hidden border-pink-200 shadow-2xl [&>button]:hidden" data-testid="notes-chain-dialog">
        <div className="bg-gradient-to-br from-pink-100 via-pink-50 to-white px-4 py-3 border-b border-pink-200">
          <div className="min-w-0">
            <DialogTitle className="text-sm font-semibold text-pink-700 tracking-tight truncate">{pitch.title}</DialogTitle>
            {brand && <p className="text-[11px] text-pink-500 truncate">{brand.logo} {brand.name}</p>}
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-1.5">
            <Input
              value={newNoteLabel}
              onChange={(e) => setNewNoteLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }}
              placeholder="label, e.g. spf"
              maxLength={24}
              className="h-9 text-xs border-pink-200 focus-visible:ring-pink-300 w-24 shrink-0"
              data-testid="notes-chain-note-label"
            />
            <Input
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }}
              placeholder="what you sent, brand response, next step…"
              className="h-9 text-xs border-pink-200 focus-visible:ring-pink-300"
              data-testid="notes-chain-note-text"
            />
            <Button
              onClick={addNote}
              disabled={!newNote.trim() && !newNoteLabel.trim()}
              className="h-9 bg-pink-500 hover:bg-pink-600 text-white text-xs px-3 shrink-0"
              data-testid="notes-chain-note-log"
            ><Plus className="w-3 h-3 mr-1" /> log</Button>
          </div>

          {entries.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic mt-3">no notes yet — every entry is date-stamped</div>
          ) : (
            <ul className="mt-3 space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="notes-chain-list">
              {entries.map((n, idx) => (
                <NoteBubble key={n.id} note={n} idx={idx} onPatch={(patch) => patchNote(n.id, patch)} onRemove={() => removeNote(n.id)} />
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="px-4 pb-4 pt-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-8 text-xs">close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Paste a list of free-text names, one per line — each becomes a lightweight "to pitch"
// bubble (NOT a real pitch yet) that lives in the box above the board/list until someone
// clicks it and actually fills in brand/talent/status via the normal new-pitch form.
const MassUploadPitchDialog = ({ open, onOpenChange, onAdded }) => {
  const [text, setText] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { if (open) setText(''); }, [open]);

  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

  const submit = async () => {
    if (!lines.length) return;
    setSaving(true);
    try {
      const { data } = await api.post('/pitch-queue/bulk', { titles: lines });
      onAdded(data);
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pitch-mass-upload-dialog">
        <DialogHeader><DialogTitle>mass upload pitches</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">paste a list of names, one per line. each shows up as a bubble in the "to pitch" box above — click one later to turn it into a real pitch.</p>
        <Textarea
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder={'Nike\nAdidas\nGymshark'}
          className="mt-2 text-sm"
          data-testid="pitch-mass-upload-textarea"
        />
        <p className="text-xs text-slate-400">{lines.length} name{lines.length === 1 ? '' : 's'} will be added</p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-8 text-xs">cancel</Button>
          <Button
            disabled={saving || !lines.length}
            onClick={submit}
            className="h-8 text-xs bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold"
            data-testid="pitch-mass-upload-submit"
          >{saving ? 'adding…' : `add ${lines.length || ''} name${lines.length === 1 ? '' : 's'}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ConvertDialog = ({ open, onOpenChange, pitch }) => {
  const { convertPitch } = usePitches();
  const [value, setValue] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { if (open) setValue(0); }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await convertPitch(pitch.id, { value: Number(value) || 0, stage: 'contracted' });
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>convert to deal</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">creates a confirmed deal on the deals board and marks this pitch as successful.</p>
        <div className="space-y-3 mt-3">
          <div>
            <Label>Deal value (GBP)</Label>
            <Input type="number" value={value || ''} onChange={(e) => setValue(e.target.value)} placeholder="0" className="mt-1" />
            <p className="text-xs text-slate-500 mt-1">You can fill in more details on the deals tab afterwards.</p>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">{saving ? 'converting…' : 'convert & create deal'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Pitching = () => {
  const { user } = useAuth();
  const { talent, brandMap, deals, brands } = useDeals();
  const { pitches, statuses, loading, updatePitch, removePitch } = usePitches();
  const { items: brandPitches, refresh: refreshBrandPitches } = useBrandPitches();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | status id
  const [brandFilter, setBrandFilter] = useState('all'); // 'all' | 'with_deals' | 'without_deals' | 'stale'
  const [tagFilter, setTagFilter] = useState([]); // array of sticker ids, [] = no tag filtering
  const [talentFilter, setTalentFilter] = useState('all'); // 'all' | talent user_id
  const [brandSort, setBrandSort] = useState('value'); // 'value' | 'alpha'
  const [dateRange, setDateRange] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [editingPitch, setEditingPitch] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesPitch, setNotesPitch] = useState(null);
  const [defaultTalent, setDefaultTalent] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertPitchState, setConvertPitchState] = useState(null);
  const [view, setView] = useState('outbound'); // 'outbound' (list) | 'board' | 'master' (brand view)
  const [contentPairs, setContentPairs] = useState(new Set()); // `${talent_id}::${brand_id}` when content posted
  const [massUploadOpen, setMassUploadOpen] = useState(false);
  const [queueItems, setQueueItems] = useState([]);
  const [queueDefaultTitle, setQueueDefaultTitle] = useState(null);
  const [convertingQueueId, setConvertingQueueId] = useState(null);

  const loadQueue = () => {
    if (!isManager) return;
    api.get('/pitch-queue').then(({ data }) => setQueueItems((data || []).slice().sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase())))).catch(() => {});
  };
  useEffect(() => { loadQueue(); }, [isManager]);

  const removeQueueItem = (itemId) => {
    setQueueItems((prev) => prev.filter((i) => i.id !== itemId));
    api.delete(`/pitch-queue/${itemId}`).catch(() => {});
  };

  const startConvertQueueItem = (item) => {
    setConvertingQueueId(item.id);
    setQueueDefaultTitle(item.title);
    setDefaultTalent(null);
    setQuickOpen(true);
  };

  useEffect(() => {
    // Load content-data once so we can flag pitches that have already produced posts.
    api.get('/content-data').then(({ data }) => {
      const s = new Set();
      (data || []).forEach((c) => {
        if (!c.talent_id) return;
        const ids = c.brand_ids && c.brand_ids.length ? c.brand_ids : (c.brand_id ? [c.brand_id] : []);
        ids.forEach((bid) => s.add(`${c.talent_id}::${bid}`));
      });
      setContentPairs(s);
    }).catch(() => {});
  }, []);

  // Brand-level rollups (matches the master grid logic) so we can filter by brand deal history
  const brandStats = useMemo(() => {
    const LOCKED = new Set(['rate_confirmed', 'contracted', 'complete']);
    const totals = {};
    const lastActivity = {};
    const pp = {}; // key `${brand_id}::${talent_id}` -> true when at least one confirmed deal exists
    const pairRevenue = {}; // key `${brand_id}::${talent_id}` -> lifetime 100% deal value
    const pairLastActivity = {}; // key `${brand_id}::${talent_id}` -> latest activity date for that pair
    const pairLastCampaign = {}; // key `${brand_id}::${talent_id}` -> latest CONFIRMED campaign date (locked deals only)
    const toISO = (v) => {
      if (!v) return null;
      if (typeof v === 'string') return v.slice(0, 10);
      try { return new Date(v).toISOString().slice(0, 10); } catch { return null; }
    };
    (deals || []).forEach((d) => {
      const bid = d.brand_id;
      if (!bid) return;
      const locked = LOCKED.has(d.stage);
      if (locked) totals[bid] = (totals[bid] || 0) + (Number(d.value) || 0);
      const best = [d.go_live_date, d.paid_in_at, d.invoiced_at, d.due_date, d.created_at]
        .map(toISO).filter(Boolean).sort().slice(-1)[0];
      if (best && (!lastActivity[bid] || best > lastActivity[bid])) lastActivity[bid] = best;
      const tids = [];
      if (d.talent_id) tids.push(d.talent_id);
      (d.talent_ids || []).forEach((t) => { if (t && !tids.includes(t)) tids.push(t); });
      tids.forEach((tid) => {
        const pk = `${bid}::${tid}`;
        if (best && (!pairLastActivity[pk] || best > pairLastActivity[pk])) pairLastActivity[pk] = best;
        if (locked) {
          pp[pk] = true;
          pairRevenue[pk] = (pairRevenue[pk] || 0) + (Number(d.value) || 0);
          if (best && (!pairLastCampaign[pk] || best > pairLastCampaign[pk])) pairLastCampaign[pk] = best;
        }
      });
    });
    return { totals, lastActivity, pp, pairRevenue, pairLastActivity, pairLastCampaign };
  }, [deals]);

  // brands engaged within the last 3 months (auto sticker)
  const engagedBrandIds = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    const s = new Set();
    (brands || []).forEach((b) => {
      if (!b.last_meeting_date) return;
      const d = new Date(b.last_meeting_date);
      if (!isNaN(d.getTime()) && d >= cutoff) s.add(b.id);
    });
    return s;
  }, [brands]);

  // Manual stickers per (brand, talent), keyed for O(1) lookup
  const stickersFor = useMemo(() => {
    const m = {};
    (brandPitches || []).forEach((bp) => { m[`${bp.brand_id}::${bp.talent_id}`] = bp.stickers || []; });
    return m;
  }, [brandPitches]);

  // Star rating per (brand, talent) — 0 (unrated) to 5
  const ratingsFor = useMemo(() => {
    const m = {};
    (brandPitches || []).forEach((bp) => { m[`${bp.brand_id}::${bp.talent_id}`] = bp.stars || 0; });
    return m;
  }, [brandPitches]);

  const setStars = async (brandId, talentId, stars) => {
    try {
      await api.post('/pitching/set-stars', { brand_id: brandId, talent_id: talentId, stars });
      refreshBrandPitches?.();
    } catch (e) { toast.error('failed to save rating'); }
  };

  const toggleSticker = async (brandId, talentId, sticker) => {
    try {
      await api.post('/pitching/toggle-sticker', { brand_id: brandId, talent_id: talentId, sticker });
      refreshBrandPitches?.();
    } catch (e) { /* silent */ }
  };

  // Custom sticker defs (managed by the user, shared across pitches)
  const [customStickers, setCustomStickers] = useState([]);
  useEffect(() => {
    api.get('/pitching/stickers').then(({ data }) => setCustomStickers(data || [])).catch(() => {});
  }, []);
  const createSticker = async (def) => {
    try {
      const { data } = await api.post('/pitching/stickers', def);
      setCustomStickers((prev) => [...prev, data]);
      return data;
    } catch { return null; }
  };
  const deleteSticker = async (id) => {
    try {
      await api.delete(`/pitching/stickers/${id}`);
      setCustomStickers((prev) => prev.filter((s) => s.id !== id));
      refreshBrandPitches?.();
    } catch { /* silent */ }
  };

  const visiblePitches = useMemo(() => {
    const s = q.trim().toLowerCase();
    const now = new Date();
    const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    const inStatus = (p) => statusFilter === 'all' || p.status === statusFilter;
    const inBrand = (p) => {
      if (brandFilter === 'all') return true;
      const bid = p.brand_id;
      if (!bid) return false;
      const tids = Array.isArray(p.talent_ids) && p.talent_ids.length ? p.talent_ids : (p.talent_id ? [p.talent_id] : []);
      if (tids.length === 0) {
        // no specific talent tagged (full roster) — can't scope to a pair, fall back to brand-wide
        const hasDeals = (brandStats.totals[bid] || 0) > 0;
        if (brandFilter === 'with_deals') return hasDeals;
        if (brandFilter === 'without_deals') return !hasDeals;
        if (brandFilter === 'stale') {
          if (!hasDeals) return false;
          const last = brandStats.lastActivity[bid];
          return Boolean(last) && new Date(last) < threeMonthsAgo;
        }
        return true;
      }
      // scoped to the actual tagged talent(s) — a brand only counts as "with previous deals"
      // for THIS pitch if one of ITS tagged talents has worked with that brand before
      const hasDealsForPair = tids.some((tid) => brandStats.pp[`${bid}::${tid}`]);
      if (brandFilter === 'with_deals') return hasDealsForPair;
      if (brandFilter === 'without_deals') return !hasDealsForPair;
      if (brandFilter === 'stale') {
        return tids.some((tid) => {
          if (!brandStats.pp[`${bid}::${tid}`]) return false;
          const last = brandStats.pairLastActivity[`${bid}::${tid}`];
          return Boolean(last) && new Date(last) < threeMonthsAgo;
        });
      }
      return true;
    };
    const inTalent = (p) => {
      if (talentFilter === 'all') return true;
      const ids = Array.isArray(p.talent_ids) && p.talent_ids.length ? p.talent_ids : (p.talent_id ? [p.talent_id] : []);
      return ids.includes(talentFilter);
    };
    const inTag = (p) => {
      if (tagFilter.length === 0) return true;
      const bid = p.brand_id;
      if (!bid) return false;
      const tids = Array.isArray(p.talent_ids) && p.talent_ids.length ? p.talent_ids : (p.talent_id ? [p.talent_id] : []);
      const pitchTags = new Set();
      if (tids.length === 0) {
        if ((brandStats.totals[bid] || 0) > 0) pitchTags.add('pp');
      } else {
        if (tids.some((tid) => brandStats.pp[`${bid}::${tid}`])) pitchTags.add('pp');
        tids.forEach((tid) => (stickersFor[`${bid}::${tid}`] || []).forEach((sk) => pitchTags.add(sk)));
      }
      if (engagedBrandIds.has(bid)) pitchTags.add('engaged');
      return tagFilter.every((t) => pitchTags.has(t));
    };
    const matches = (p) => {
      if (!inStatus(p)) return false;
      if (!inBrand(p)) return false;
      if (!inTalent(p)) return false;
      if (!inTag(p)) return false;
      if (!s) return true;
      const brand = brandMap[p.brand_id];
      return (p.title || '').toLowerCase().includes(s) ||
        (brand?.name || '').toLowerCase().includes(s) ||
        (p.notes || '').toLowerCase().includes(s);
    };
    return filterByRange(pitches.filter(matches), dateRange, 'pitched_date');
  }, [pitches, brandMap, q, statusFilter, brandFilter, tagFilter, talentFilter, brandStats, stickersFor, engagedBrandIds, dateRange]);

  const groups = useMemo(() => {
    const byTalent = {};
    (isManager ? talent : talent.filter((t) => t.user_id === user?.user_id)).forEach((t) => { byTalent[t.user_id] = { talent: t, pitches: [] }; });
    // "full roster" bucket: pitches tagged to nobody in particular, OR to 2+ creators at once
    byTalent['__none__'] = { talent: null, pitches: [] };
    visiblePitches.forEach((p) => {
      const ids = Array.isArray(p.talent_ids) && p.talent_ids.length ? p.talent_ids : (p.talent_id ? [p.talent_id] : []);
      const key = (ids.length === 1 && byTalent[ids[0]]) ? ids[0] : '__none__';
      byTalent[key].pitches.push(p);
    });
    // sort pitches inside each talent group by brand £ value (desc) or brand name (A-Z)
    Object.values(byTalent).forEach((g) => {
      g.pitches.sort((a, b) => {
        if (brandSort === 'alpha') {
          const an = (brandMap[a.brand_id]?.name || '~').toLowerCase();
          const bn = (brandMap[b.brand_id]?.name || '~').toLowerCase();
          return an.localeCompare(bn);
        }
        const aTid = a.talent_id || (Array.isArray(a.talent_ids) && a.talent_ids[0]);
        const bTid = b.talent_id || (Array.isArray(b.talent_ids) && b.talent_ids[0]);
        const av = (aTid && a.brand_id) ? (brandStats.pairRevenue[`${a.brand_id}::${aTid}`] || 0) : 0;
        const bv = (bTid && b.brand_id) ? (brandStats.pairRevenue[`${b.brand_id}::${bTid}`] || 0) : 0;
        return bv - av;
      });
    });
    return Object.values(byTalent).filter((g) => g.pitches.length > 0 || (isManager && g.talent && brandFilter === 'all' && (talentFilter === 'all' || g.talent.user_id === talentFilter)));
  }, [visiblePitches, talent, brandMap, brandFilter, brandSort, brandStats, isManager, user, talentFilter]);

  const openNew = (talentId) => { setDefaultTalent(talentId || null); setQueueDefaultTitle(null); setConvertingQueueId(null); setQuickOpen(true); };
  const openEdit = (p) => { setEditingPitch(p); setDialogOpen(true); };
  const openNotes = (p) => { setNotesPitch(p); setNotesOpen(true); };
  const openConvert = (p) => { setConvertPitchState(p); setConvertOpen(true); };

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">pitching</h1>
          <p className="text-sm text-slate-500 mt-1">track outbound pitches per talent. convert wins into confirmed deals with one click.</p>
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <Button onClick={() => setMassUploadOpen(true)} variant="outline" className="border-mint-300 text-slate-700 hover:bg-mint-50" data-testid="pitch-mass-upload-btn">
              <Upload className="w-4 h-4 mr-1.5" /> mass upload pitch
            </Button>
            <Button onClick={() => openNew(null)} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
              <Plus className="w-4 h-4 mr-1.5" /> new pitch
            </Button>
          </div>
        )}
      </header>

      {isManager && queueItems.length > 0 && (
        <div className="mb-6 bg-mint-50/50 border border-mint-200 rounded-2xl p-4" data-testid="pitch-queue-box">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2.5">to pitch</div>
          <div className="flex flex-wrap gap-2">
            {queueItems.map((item) => (
              <div key={item.id} className="group inline-flex items-center gap-1 rounded-full border border-mint-300 bg-white pl-3 pr-1.5 py-1" data-testid={`pitch-queue-item-${item.id}`}>
                <button type="button" onClick={() => startConvertQueueItem(item)} className="text-xs font-medium text-slate-700 hover:text-mint-700">
                  {item.title}
                </button>
                <button
                  type="button"
                  onClick={() => removeQueueItem(item.id)}
                  className="opacity-0 group-hover:opacity-100 shrink-0 text-slate-300 hover:text-red-600 rounded-full p-0.5"
                  title="remove"
                  data-testid={`pitch-queue-item-remove-${item.id}`}
                ><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 mb-5" data-testid="pitching-view-tabs">
        <button
          onClick={() => setView('outbound')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${view === 'outbound' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
          data-testid="tab-outbound"
        >list</button>
        <button
          onClick={() => setView('board')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${view === 'board' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
          data-testid="tab-board"
        >board</button>
        {isManager && (
          <button
            onClick={() => setView('master')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${view === 'master' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            data-testid="tab-master-grid"
          >brand view</button>
        )}
      </div>

      {view === 'master' && isManager ? (
        <MasterPitchingGrid />
      ) : (
      <>
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search pitches, brands, notes…" className="pl-9 h-10 bg-white" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 w-44 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">all statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {isManager && (
          <Select value={talentFilter} onValueChange={setTalentFilter}>
            <SelectTrigger className="h-10 w-40 bg-white" data-testid="pitch-talent-filter"><SelectValue placeholder="talent" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all talent</SelectItem>
              {talent.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-5">
        <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5" data-testid="talent-view-brand-filter">
          {[
            { id: 'all', label: 'all brands' },
            { id: 'with_deals', label: 'with previous deals' },
            { id: 'without_deals', label: 'without previous deals' },
            { id: 'stale', label: 'to retain' },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setBrandFilter(opt.id)}
              className={`px-3 h-8 text-[11px] font-semibold rounded-full transition ${brandFilter === opt.id ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`talent-brand-filter-${opt.id}`}
            >{opt.label}</button>
          ))}
        </div>

        <div className="inline-flex items-center gap-1 flex-wrap" data-testid="talent-view-tag-filter">
          {[...STICKERS, ...customStickers].map((s) => {
            const active = tagFilter.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setTagFilter((prev) => prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id])}
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-[11px] font-semibold transition"
                style={active
                  ? { background: s.bg, borderWidth: 1, borderStyle: 'solid', borderColor: s.border, color: s.ink }
                  : { background: 'transparent', borderWidth: 1, borderStyle: 'solid', borderColor: '#e2e8f0', color: '#94a3b8' }}
                title={active ? `showing only "${s.label}" tagged pitches — click to remove` : `filter by "${s.label}"`}
                data-testid={`talent-tag-filter-${s.id}`}
              >{s.emoji && <span>{s.emoji}</span>}{s.label}</button>
            );
          })}
          {tagFilter.length > 0 && (
            <button
              type="button"
              onClick={() => setTagFilter([])}
              className="text-[11px] text-slate-400 hover:text-slate-700 px-1.5"
              data-testid="talent-tag-filter-clear"
            >clear</button>
          )}
        </div>

        {view === 'outbound' && (
        <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5 ml-1" data-testid="talent-view-brand-sort">
          {[
            { id: 'value', label: 'by £ value' },
            { id: 'alpha', label: 'A–Z' },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setBrandSort(opt.id)}
              className={`px-3 h-8 text-[11px] font-semibold rounded-full transition ${brandSort === opt.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`talent-brand-sort-${opt.id}`}
            >{opt.label}</button>
          ))}
        </div>
        )}
      </div>

      {view === 'board' ? (
        loading ? (
          <div className="text-slate-400 text-sm py-12 text-center">loading…</div>
        ) : visiblePitches.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
            <p className="text-slate-500 text-sm">no pitches yet.</p>
            {isManager && <Button onClick={() => openNew(null)} className="mt-4 bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold"><Plus className="w-4 h-4 mr-1.5" /> add your first pitch</Button>}
          </div>
        ) : (
          <PitchBoard pitches={visiblePitches} brandMap={brandMap} talent={talent} ratingsFor={ratingsFor} onCardClick={openEdit} onStars={setStars} onConvert={openConvert} />
        )
      ) : loading ? (
        <div className="text-slate-400 text-sm py-12 text-center">loading…</div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <p className="text-slate-500 text-sm">no pitches yet.</p>
          {isManager && <Button onClick={() => openNew(null)} className="mt-4 bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold"><Plus className="w-4 h-4 mr-1.5" /> add your first pitch</Button>}
        </div>
      ) : (
        <Accordion type="multiple" defaultValue={groups.map((g) => g.talent?.user_id || '__none__')} className="space-y-3">
          {groups.map((g) => {
            const key = g.talent?.user_id || '__none__';
            const talentId = g.talent?.user_id;
            const liveCount = g.pitches.filter((p) => !['declined', 'converted'].includes(p.status)).length;
            const convertedBrandIds = new Set(g.pitches.filter((p) => p.status === 'converted').map((p) => p.brand_id));
            const ppBrandIds = new Set(
              g.pitches
                .filter((p) => p.status !== 'converted' && p.brand_id && talentId)
                .filter((p) => {
                  const pk = `${p.brand_id}::${talentId}`;
                  return Boolean(brandStats.pp[pk]) || (stickersFor[pk] || []).includes('pp');
                })
                .map((p) => p.brand_id)
            );
            const convertedCount = convertedBrandIds.size + ppBrandIds.size;
            return (
              <AccordionItem key={key} value={key} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <AccordionTrigger className="px-4 py-3 hover:no-underline border-b border-pink-200 hover:brightness-95 transition" style={{ background: '#d394ab' }}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {g.talent ? (
                      <InitialsAvatar name={g.talent.name} size={32} />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white/30 border border-dashed border-white/50" />
                    )}
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-semibold text-white truncate">{g.talent?.name || 'full roster'}</div>
                      <div className="text-xs text-white/80">{liveCount} live • {convertedCount} won • {g.pitches.length} total</div>
                    </div>
                    {isManager && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); openNew(g.talent?.user_id || null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openNew(g.talent?.user_id || null); } }}
                        className="inline-flex items-center gap-1 text-xs text-white/95 hover:text-white bg-white/15 hover:bg-white/25 rounded-full px-2.5 py-1 mr-3 cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" /> add pitch
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="border-t border-slate-100">
                  {g.pitches.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-slate-400">no pitches for this talent yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                      <thead className="bg-slate-50">
                        <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 whitespace-nowrap">
                          <th className="text-center font-semibold px-4 py-3" style={{ width: 150 }}>brand</th>
                          <th className="text-center font-semibold px-4 py-3" style={{ width: 300 }}>tags</th>
                          <th className="text-center font-semibold px-4 py-3">notes</th>
                          <th className="text-center font-semibold px-4 py-3" style={{ width: 140 }}>last campaign</th>
                          <th className="text-center font-semibold px-4 py-3" style={{ width: 120 }}>brand revenue</th>
                          <th className="text-center font-semibold px-4 py-3" style={{ width: 130 }}>rating</th>
                          <th style={{ width: 56 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.pitches.map((p) => {
                          const brand = brandMap[p.brand_id];
                          const sortedNotes = Array.isArray(p.note_entries)
                            ? [...p.note_entries].filter((n) => !n.hidden_from_tracker).sort((a, b) => (b.created_at || b.date || '').localeCompare(a.created_at || a.date || ''))
                            : [];
                          const pitchTalentIds = Array.isArray(p.talent_ids) && p.talent_ids.length ? p.talent_ids : (p.talent_id ? [p.talent_id] : []);
                          const taggedTalents = isManager ? pitchTalentIds.map((tid) => talent.find((t) => t.user_id === tid)).filter(Boolean) : [];
                          return (
                            <tr key={p.id} className="border-t border-slate-100 hover:bg-pink-50/30 cursor-pointer transition-colors" onClick={() => openEdit(p)}>
                              <td className="px-4 py-2.5 align-top text-center">
                                <div className="inline-flex items-center gap-1.5 justify-center">
                                  {brand ? (
                                    <span
                                      className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-semibold"
                                      style={{ background: '#f3ecfb', color: '#5b21b6', border: '1px solid #d3c1e1' }}
                                      title={p.title}
                                    >
                                      {brand.logo && <span className="text-sm leading-none">{brand.logo}</span>}
                                      <span className="truncate max-w-[160px]">{brand.name}</span>
                                    </span>
                                  ) : <span className="italic text-slate-400 text-xs" title={p.title}>no brand</span>}
                                  {taggedTalents.length > 0 && !g.talent && (
                                    <div className="flex -space-x-1.5 shrink-0" data-testid={`pitch-tagged-talents-${p.id}`} title={taggedTalents.map((t) => t.name).join(', ')}>
                                      {taggedTalents.slice(0, 4).map((t) => (
                                        <div key={t.user_id} className="ring-2 ring-white rounded-full">
                                          <InitialsAvatar name={t.name} size={16} />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 align-top text-center" style={{ minWidth: 320 }}>
                                <div className="flex items-center justify-center gap-1">
                                  <div className="flex items-center gap-1 overflow-x-auto" style={{ maxWidth: '100%' }} onClick={(e) => e.stopPropagation()}>
                                    <StickersRail
                                      hasPP={Boolean(brandStats.pp[`${p.brand_id}::${p.talent_id}`])}
                                      hasEngaged={engagedBrandIds.has(p.brand_id)}
                                      manualStickers={stickersFor[`${p.brand_id}::${p.talent_id}`] || []}
                                      canEdit={isManager}
                                      onToggle={(s) => toggleSticker(p.brand_id, p.talent_id, s)}
                                      customStickers={customStickers}
                                      onCreateSticker={createSticker}
                                      onDeleteSticker={deleteSticker}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-2.5 align-top text-center w-full" data-testid={`pitch-last-note-${p.id}`}>
                                <div className="flex items-center justify-center gap-1">
                                  {sortedNotes.length > 0 ? (
                                    <div
                                      className="flex items-center gap-1 overflow-x-auto"
                                      style={{ maxWidth: '100%' }}
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`pitch-note-chips-${p.id}`}
                                    >
                                      {sortedNotes.map((n) => (
                                        <button
                                          key={n.id}
                                          type="button"
                                          onClick={() => openNotes(p)}
                                          title={n.text || n.label}
                                          className="shrink-0 whitespace-nowrap inline-flex items-center h-6 px-2 rounded-full text-[10px] font-bold uppercase tracking-wide cursor-pointer hover:brightness-95 transition"
                                          style={{ background: NOTE_PALETTES[0].bg, color: NOTE_PALETTES[0].ink, border: `1px solid ${NOTE_PALETTES[0].border}` }}
                                          data-testid={`pitch-note-chip-${n.id}`}
                                        >{n.label || (n.text ? `${n.text.slice(0, 10)}…` : 'note')}</button>
                                      ))}
                                    </div>
                                  ) : (
                                    <span
                                      className="text-slate-300 text-xs italic cursor-pointer hover:text-slate-400"
                                      onClick={(e) => { e.stopPropagation(); openNotes(p); }}
                                    >no notes yet</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openNotes(p); }}
                                    title="quick-add a note"
                                    className="shrink-0 w-5 h-5 rounded-full border border-pink-200 text-pink-500 hover:bg-pink-50 hover:border-pink-400 flex items-center justify-center transition"
                                    data-testid={`pitch-note-add-${p.id}`}
                                  ><Plus className="w-3 h-3" /></button>
                                </div>
                              </td>
                              <td className="px-4 py-2.5 align-top text-center" onClick={(e) => e.stopPropagation()}>
                                {(() => {
                                  const revTalentId = p.talent_id || pitchTalentIds[0];
                                  const autoDate = (revTalentId && p.brand_id) ? (brandStats.pairLastCampaign[`${p.brand_id}::${revTalentId}`] || null) : null;
                                  return <div className="flex items-center justify-center"><LastCampaignCell pitch={p} autoDate={autoDate} /></div>;
                                })()}
                              </td>
                              <td className="px-4 py-2.5 align-top text-center">
                                {(() => {
                                  const revTalentId = p.talent_id || pitchTalentIds[0];
                                  const revenue = (revTalentId && p.brand_id) ? (brandStats.pairRevenue[`${p.brand_id}::${revTalentId}`] || 0) : 0;
                                  return revenue > 0 ? (
                                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700" title="100% deal value from this brand with this talent, all time" data-testid={`pitch-brand-revenue-${p.id}`}>
                                      {fmtMoney(revenue)}
                                    </span>
                                  ) : (
                                    <span className="text-slate-300 text-xs" data-testid={`pitch-brand-revenue-${p.id}`}>—</span>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-2.5 align-top text-center" onClick={(e) => e.stopPropagation()}>
                                {(() => {
                                  const revTalentId = p.talent_id || pitchTalentIds[0];
                                  const stars = (revTalentId && p.brand_id) ? (ratingsFor[`${p.brand_id}::${revTalentId}`] || 0) : 0;
                                  return (
                                    <div className="flex items-center justify-center">
                                      <Stars value={stars} onChange={revTalentId ? (v) => setStars(p.brand_id, revTalentId, v) : undefined} testId={`list-stars-${p.id}`} />
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-2 py-2.5 text-center align-top">
                                {isManager && (
                                  <button onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this pitch?')) removePitch(p.id); }} className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      </>
      )}

      <PitchDialog open={dialogOpen} onOpenChange={setDialogOpen} pitch={editingPitch} defaultTalentId={defaultTalent} ratingsFor={ratingsFor} onStars={setStars} />
      <NotesChainDialog
        open={notesOpen}
        onOpenChange={setNotesOpen}
        pitch={notesPitch}
        brand={notesPitch ? brandMap[notesPitch.brand_id] : null}
      />
      <QuickAddPitch
        open={quickOpen}
        onOpenChange={(v) => { setQuickOpen(v); if (!v) { setQueueDefaultTitle(null); setConvertingQueueId(null); } }}
        defaultTalentId={defaultTalent}
        defaultTitle={queueDefaultTitle}
        onSaved={() => { if (convertingQueueId) removeQueueItem(convertingQueueId); }}
      />
      <MassUploadPitchDialog open={massUploadOpen} onOpenChange={setMassUploadOpen} onAdded={(items) => setQueueItems((prev) => [...prev, ...items].sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase())))} />
      {convertPitchState && <ConvertDialog open={convertOpen} onOpenChange={setConvertOpen} pitch={convertPitchState} />}
    </div>
  );
};

export default Pitching;
